import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  GridLegacy as Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  SupervisorAccount as AdminIcon,
  VerifiedUser as VerifiedIcon,
  Assignment as ReportIcon,
  Notifications as NotificationIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import pointsService from '../services/pointsService';

interface User {
  uid: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
  xmAffiliationVerified: boolean;
  createdAt: any;
  lastLogin: any;
  currentXmLevel?: string;
  xmPoints?: number;
  totalCommission?: number;
}

interface UserStats {
  totalUsers: number;
  adminUsers: number;
  verifiedUsers: number;
  bronzeUsers: number;
  silverUsers: number;
  goldUsers: number;
  totalCommission: number;
  totalXmPoints: number;
}

export const AdminDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    adminUsers: 0,
    verifiedUsers: 0,
    bronzeUsers: 0,
    silverUsers: 0,
    goldUsers: 0,
    totalCommission: 0,
    totalXmPoints: 0,
  });
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [topUsers, setTopUsers] = useState<User[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [calculating, setCalculating] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.isAdmin === true) {
            setIsAdmin(true);
          } else {
            setError('Access denied. Admin privileges required.');
            setTimeout(() => navigate('/dashboard'), 2000);
          }
        } else {
          setError('User profile not found.');
          setTimeout(() => navigate('/dashboard'), 2000);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin status.');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!isAdmin) return;

      try {
        setLoading(true);

        // Get all users
        const usersQuery = collection(db, 'users');
        const usersSnapshot = await getDocs(usersQuery);
        const users: User[] = [];

        // Get all user progress data
        const userProgressQuery = collection(db, 'userProgress');
        const userProgressSnapshot = await getDocs(userProgressQuery);
        const userProgressMap = new Map();
        
        userProgressSnapshot.forEach((doc) => {
          const progressData = doc.data();
          userProgressMap.set(doc.id, progressData);
        });

        let stats: UserStats = {
          totalUsers: 0,
          adminUsers: 0,
          verifiedUsers: 0,
          bronzeUsers: 0,
          silverUsers: 0,
          goldUsers: 0,
          totalCommission: 0,
          totalXmPoints: 0,
        };

        usersSnapshot.forEach((doc) => {
          const userData = doc.data() as User;
          userData.uid = doc.id;
          
          // Merge user progress data
          const progressData = userProgressMap.get(doc.id);
          if (progressData) {
            userData.currentXmLevel = progressData.currentXmLevel;
            userData.xmPoints = progressData.xmPoints;
            userData.totalCommission = progressData.totalCommission;
          }
          
          users.push(userData);

          // Calculate statistics using the merged data
          stats.totalUsers++;
          if (userData.isAdmin) stats.adminUsers++;
          if (userData.xmAffiliationVerified) stats.verifiedUsers++;
          if (userData.currentXmLevel === 'bronze') stats.bronzeUsers++;
          if (userData.currentXmLevel === 'silver') stats.silverUsers++;
          if (userData.currentXmLevel === 'gold') stats.goldUsers++;
          if (userData.totalCommission) stats.totalCommission += userData.totalCommission;
          if (userData.xmPoints) stats.totalXmPoints += userData.xmPoints;
        });

        setUserStats(stats);

        // Get recent users (last 10) with progress data
        const recentUsersQuery = query(
          collection(db, 'users'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const recentSnapshot = await getDocs(recentUsersQuery);
        const recentUsersData: User[] = [];
        recentSnapshot.forEach((doc) => {
          const userData = doc.data() as User;
          userData.uid = doc.id;
          
          // Merge user progress data for recent users too
          const progressData = userProgressMap.get(doc.id);
          if (progressData) {
            userData.currentXmLevel = progressData.currentXmLevel;
            userData.xmPoints = progressData.xmPoints;
            userData.totalCommission = progressData.totalCommission;
          }
          
          recentUsersData.push(userData);
        });
        setRecentUsers(recentUsersData);

        // Get top users by XM points (now using merged data)
        const topUsersData = users
          .filter(user => user.xmPoints && user.xmPoints > 0)
          .sort((a, b) => (b.xmPoints || 0) - (a.xmPoints || 0))
          .slice(0, 5);
        setTopUsers(topUsersData);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [isAdmin]);

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleToggleAdminStatus = async () => {
    if (!selectedUser) return;

    try {
      const newAdminStatus = !selectedUser.isAdmin;
      await updateDoc(doc(db, 'users', selectedUser.uid), {
        isAdmin: newAdminStatus,
        updatedAt: serverTimestamp(),
      });

      setSuccess(`User ${newAdminStatus ? 'promoted to' : 'removed from'} admin role.`);
      
      // Reload data
      window.location.reload();
    } catch (error) {
      console.error('Error updating admin status:', error);
      setError('Failed to update admin status.');
    }
    handleMenuClose();
  };

  const handleRecalculateXmPoints = async () => {
    try {
      setCalculating(true);
      setError(null);
      setSuccess(null);

      // Get all users
      const usersQuery = collection(db, 'users');
      const usersSnapshot = await getDocs(usersQuery);
      let calculatedCount = 0;

      for (const userDoc of usersSnapshot.docs) {
        try {
          const userData = userDoc.data();
          await pointsService.calculateXmLevel(userDoc.id, userData);
          calculatedCount++;
        } catch (userError) {
          console.error(`Error calculating XM Points for user ${userDoc.id}:`, userError);
          // Continue with other users even if one fails
        }
      }

      setSuccess(`Successfully recalculated XM Points for ${calculatedCount} users.`);
      
      // Reload dashboard data to show updated stats
      window.location.reload();
    } catch (error) {
      console.error('Error recalculating XM Points:', error);
      setError('Failed to recalculate XM Points.');
    } finally {
      setCalculating(false);
    }
  };



  const getLevelIcon = (level?: string) => {
    switch (level) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '⭐';
    }
  };

  const getLevelColor = (level?: string) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return '#9e9e9e';
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error || 'Access denied. Admin privileges required.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DashboardIcon sx={{ fontSize: 40 }} />
              Admin Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Manage users, view statistics, and configure system settings
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleRecalculateXmPoints}
            disabled={calculating}
            sx={{ mt: 1 }}
          >
            {calculating ? 'Calculating...' : 'Recalculate XM Points'}
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Total Users
                    </Typography>
                    <Typography variant="h4">
                      {userStats.totalUsers}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <PeopleIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Verified Users
                    </Typography>
                    <Typography variant="h4">
                      {userStats.verifiedUsers}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <VerifiedIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Admin Users
                    </Typography>
                    <Typography variant="h4">
                      {userStats.adminUsers}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <AdminIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="h6">
                      Total XM Points
                    </Typography>
                    <Typography variant="h4">
                      {userStats.totalXmPoints.toLocaleString()}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <TrophyIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Level Distribution */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrophyIcon />
                  XM Loyalty Level Distribution
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="h3">🥉</Typography>
                      <Typography variant="h5">{userStats.bronzeUsers}</Typography>
                      <Typography variant="body2" color="text.secondary">Bronze</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="h3">🥈</Typography>
                      <Typography variant="h5">{userStats.silverUsers}</Typography>
                      <Typography variant="body2" color="text.secondary">Silver</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="h3">🥇</Typography>
                      <Typography variant="h5">{userStats.goldUsers}</Typography>
                      <Typography variant="body2" color="text.secondary">Gold</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="h3">⭐</Typography>
                      <Typography variant="h5">{userStats.totalUsers - userStats.bronzeUsers - userStats.silverUsers - userStats.goldUsers}</Typography>
                      <Typography variant="body2" color="text.secondary">Unranked</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>



        {/* Quick Actions */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<PeopleIcon />}
                      onClick={() => navigate('/admin/users')}
                      sx={{ height: 56 }}
                    >
                      Manage Users
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<SettingsIcon />}
                      onClick={() => navigate('/admin/loyalty-config')}
                      sx={{ height: 56 }}
                    >
                      Loyalty Config
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ReportIcon />}
                      onClick={() => navigate('/admin/reports')}
                      sx={{ height: 56 }}
                    >
                      View Reports
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<NotificationIcon />}
                      onClick={() => navigate('/admin/notifications')}
                      sx={{ height: 56 }}
                    >
                      Send Notifications
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Users and Top Users */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Users
                </Typography>
                <List>
                  {recentUsers.slice(0, 5).map((user) => (
                    <ListItem
                      key={user.uid}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={(e) => handleUserMenuClick(e, user)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: user.isAdmin ? 'warning.main' : 'primary.main' }}>
                          {user.isAdmin ? <AdminIcon /> : <PeopleIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {user.displayName || user.email}
                            {user.xmAffiliationVerified && (
                              <Chip size="small" icon={<CheckIcon />} label="Verified" color="success" />
                            )}
                            {user.isAdmin && (
                              <Chip size="small" icon={<AdminIcon />} label="Admin" color="warning" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption">{user.email}</Typography>
                            {user.currentXmLevel && (
                              <Chip
                                size="small"
                                label={getLevelIcon(user.currentXmLevel)}
                                sx={{ 
                                  bgcolor: getLevelColor(user.currentXmLevel) + '20',
                                  color: getLevelColor(user.currentXmLevel),
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate('/admin/users')}>
                  View All Users
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Users by XM Points
                </Typography>
                <List>
                  {topUsers.map((user, index) => (
                    <ListItem key={user.uid}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'primary.main' }}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {user.displayName || user.email}
                            {user.currentXmLevel && (
                              <Chip
                                size="small"
                                label={getLevelIcon(user.currentXmLevel)}
                                sx={{ 
                                  bgcolor: getLevelColor(user.currentXmLevel) + '20',
                                  color: getLevelColor(user.currentXmLevel),
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={`${user.xmPoints || 0} XM Points`}
                      />
                    </ListItem>
                  ))}
                  {topUsers.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No users with XM Points yet"
                        secondary="Users will appear here once they start earning points"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* User Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => navigate(`/admin/users/${selectedUser?.uid}`)}>
            View Profile
          </MenuItem>
          <MenuItem onClick={handleToggleAdminStatus}>
            {selectedUser?.isAdmin ? 'Remove Admin' : 'Make Admin'}
          </MenuItem>
          <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
            Block User
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
}; 